import {
  Autocomplete,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Modal,
  Select,
  TextField
} from "@mui/material";
import React, { useEffect, useState } from "react";
import {
  ClientInterface,
  ClientOption,
  WorkOrderInterface
} from "../../common/interfaces";
import { extractErrorMessage } from "../../common/utils/axios.isAxiosError";
import { useAuth } from "../../hooks/useAuth";
import { usePageToast } from "../../hooks/usePageToast";
import api from "../../services/api";
import {
  WorkOrderServiceType,
  WorkOrderServiceTypeBr
} from "../enum/workOrderServiceTypeEnum";
import PageToaster from "../toaster/PageToaster";
// import { ToastError, ToastSuccess } from "../toaster/toast.utils";

interface WorkOrderCreateModalProps {
  open: boolean;
  onClose: () => void;
  onWorkOrderCreated: () => void;
  order?: WorkOrderInterface;
}

const ORDER_BY: "NAME" | "DATE" = "NAME";

const WorkOrderCreateModal: React.FC<WorkOrderCreateModalProps> = ({
  open,
  onClose,
  onWorkOrderCreated,
  order
}) => {
  const [startDate, setStartDate] = useState("");
  const [serviceType, setServiceType] =
    useState<keyof typeof WorkOrderServiceType>("INTERNAL");

  const [clientId, setClientId] = useState<number | "">("");
  const [clientOption, setClientOption] = useState<ClientOption | null>(null);
  const [clients, setClients] = useState<ClientOption[]>([]);
  const [loadingClients, setLoadingClients] = useState(false);

  const [isUpdate, setIsUpdate] = useState(false);

  const [search, setSearch] = useState("");
  const [inputValue, setInputValue] = useState("");

  const { user_id } = useAuth();

  const containerId = "toaster-workorder-create-update";
  const { autoSuccess, autoError, autoInfo, autoWarn } = usePageToast(
    containerId,
    true
  );

  // Debounce simples
  const debouncedSearch = useDebounce(search, 400);

  // Pré-carregar dados ao abrir/editar
  useEffect(() => {
    if (order) {
      console.log("order", order);
      setStartDate(order.start_date?.slice(0, 10) ?? "");
      setServiceType(order.service_type as keyof typeof WorkOrderServiceType);

      const basic = order.client
        ? ({
            client_id: order.client.client_id,
            client_name: order.client.client_name
          } as ClientOption)
        : null;

      setClientOption(basic);
      setClientId(order.client?.client_id ?? "");
      setInputValue(order.client?.client_name ?? "");
      setIsUpdate(true);
    } else {
      setStartDate("");
      setServiceType("INTERNAL");
      setClientId("");
      setClientOption(null);
      setInputValue("");
      setIsUpdate(false);
    }
  }, [order, open]);

  useEffect(() => {
    if (!open) return;

    let ignore = false;
    (async () => {
      setLoadingClients(true);
      try {
        const res = await api.get("/client", {
          params: {
            page: 1,
            limit: 8,
            sort: "ASC",
            orderBy: ORDER_BY,
            search: debouncedSearch || undefined,
            showActives: true
          }
        });

        const items: ClientInterface[] = Array.isArray(res.data?.items)
          ? res.data.items
          : [];

        const mapped: ClientOption[] = items.map((c) => ({
          client_id: c.client_id,
          client_name: c.client_name,
          // se quiser, pode manter extras:
          client_email: c.client_email,
          client_is_company: (c as any).client_is_company,
          client_status: (c as any).client_status
        }));

        if (!ignore) setClients(mapped);
      } catch (err) {
        if (!ignore) {
          console.error("Erro ao buscar clientes:", err);
          setClients([]);
        }
      } finally {
        if (!ignore) setLoadingClients(false);
      }
    })();

    return () => {
      ignore = true;
    };
  }, [debouncedSearch, open]);

  const handleSubmit = async () => {
    if (!startDate || !clientId) {
      autoError("Preencha todos os campos obrigatórios.");
      return;
    }

    const payload = {
      start_date: new Date(startDate),
      service_type: serviceType,
      clientId,
      userId: user_id
    };

    try {
      if (isUpdate && order) {
        await api.put(`/work-order/${order.work_order_id}`, payload);
        autoSuccess("Ordem de serviço atualizada com sucesso!");
      } else {
        await api.post("/work-order", payload);
        autoSuccess("Ordem de serviço criada com sucesso!");
      }

      onWorkOrderCreated();
      onClose();
    } catch (error) {
      const msg = extractErrorMessage(error);
      console.error("Erro ao salvar ordem de serviço:", error);
      autoError(msg);
    }
  };

  return (
    <>
      <PageToaster containerId={containerId} />
      <Modal
        open={open}
        onClose={onClose}
        aria-labelledby="create-workorder-modal"
      >
        <div
          style={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            backgroundColor: "var(--surface)",
            border: "1px solid var(--border-strong)",
            color: "var(--text)",
            padding: "20px",
            borderRadius: "8px",
            boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
            width: "500px",
            maxHeight: "90vh",
            overflowY: "auto"
          }}
        >
          <h2>
            {isUpdate ? "Editar Ordem de Serviço" : "Nova Ordem de Serviço"}
          </h2>

          <TextField
            fullWidth
            label="Data de Início"
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            margin="normal"
            InputLabelProps={{ shrink: true }}
            required
          />

          <FormControl fullWidth margin="normal">
            <InputLabel>Tipo de Serviço</InputLabel>
            <Select
              value={serviceType}
              label="Tipo de Serviço"
              onChange={(e) =>
                setServiceType(
                  e.target.value as keyof typeof WorkOrderServiceTypeBr
                )
              }
            >
              {Object.entries(WorkOrderServiceTypeBr).map(([key, label]) => (
                <MenuItem key={key} value={key}>
                  {label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Autocomplete<ClientOption>
            sx={{ marginTop: "20px" }}
            options={clients}
            loading={loadingClients}
            value={clientOption}
            onChange={(_, newVal) => {
              setClientOption(newVal);
              setClientId(newVal ? newVal.client_id : "");
              setInputValue(newVal ? newVal.client_name : "");
            }}
            inputValue={inputValue}
            onInputChange={(_, newInput) => {
              setInputValue(newInput);
              setSearch(newInput);
            }}
            isOptionEqualToValue={(opt, val) => opt.client_id === val.client_id}
            getOptionLabel={(opt) => opt?.client_name ?? ""}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Cliente"
                placeholder="Digite para buscar..."
                fullWidth
                required
                InputLabelProps={{ shrink: !!inputValue }}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loadingClients ? <CircularProgress size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  )
                }}
              />
            )}
            fullWidth
          />

          <div style={{ marginTop: "20px", textAlign: "right" }}>
            <Button
              onClick={onClose}
              style={{ marginRight: "10px" }}
              color="secondary"
            >
              Cancelar
            </Button>
            <Button onClick={handleSubmit} color="primary" variant="contained">
              {isUpdate ? "Atualizar" : "Cadastrar"}
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default WorkOrderCreateModal;

// Debounce simples
function useDebounce<T>(value: T, delay = 400): T {
  const [debounced, setDebounced] = useState<T>(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(id);
  }, [value, delay]);
  return debounced;
}
